/**
 * QG runtime accessibility enhancement for the vendor NiceSelect widget.
 *
 * Defect: W5-A11Y-D10 (2026-07-31). Ignite's bundled NiceSelect (assets/global.js,
 * `window.renderSelects`) replaces every `select.styled-select` /
 * `select.styled-select-xs` with a div soup:
 *
 *   <div class="nice-select ..." tabindex="0">
 *     <span class="current">Best selling</span>
 *     <div class="nice-select-dropdown"><ul class="nice-list"><li class="option">…
 *
 * That control is keyboard-focusable but carries NO role, NO accessible name and
 * NO expanded state, so assistive technology announces it as an unlabelled group
 * of text; and NiceSelect's own key handler explicitly no-ops on Space
 * (`else if (32 === e.keyCode && s) return !1;` — and Space while CLOSED matches
 * no branch at all), so the one key most users press to open a select does
 * nothing except scroll the page. WCAG 4.1.2 (Name, Role, Value) and 2.1.1
 * (Keyboard).
 *
 * `assets/global.js` is a vendor bundle and is out of this lane's edit scope, so
 * the repair is applied at runtime, from outside, without patching the widget:
 * this file only ADDS ARIA to the DOM NiceSelect already produced and adds the
 * missing Space binding by synthesising the click NiceSelect already listens for.
 * Nothing here changes the widget's own logic, its selection model, or the
 * underlying <select> value — remove this file and the widget behaves exactly as
 * it did before.
 *
 * SCOPE NOTE: despite the `qg-header-` filename (the naming this lane is allowed
 * to create), the widget it repairs is NOT part of the header. NiceSelect is used
 * by the collection/search sort controls, the product variant picker, the compare
 * switcher and the contact form. The file is loaded from sections/qg-header.liquid
 * purely because that section renders in the header group on every page, which
 * makes it the one in-scope place that can reach every NiceSelect instance. The
 * durable fix belongs in the vendor bundle — see the lane E report.
 *
 * Idempotent: an instance already carrying `data-qg-a11y` is skipped, so this can
 * coexist with (and be superseded by) a future vendor-side fix.
 */
(function () {
  'use strict';

  var NS_SELECTOR = '.nice-select';
  var DONE_ATTR = 'data-qg-a11y';
  var uid = 0;

  function nextId(prefix) {
    uid += 1;
    return prefix + '-' + uid;
  }

  /* The <select> NiceSelect hid: it is inserted `afterend` of the original, so
     the original is always the immediately preceding sibling. */
  function nativeSelectFor(root) {
    var prev = root.previousElementSibling;
    return prev && prev.tagName === 'SELECT' ? prev : null;
  }

  /* Accessible name, taken only from a name the page ALREADY renders for the
     native control — never invented here. Order: <label for>, aria-label,
     wrapping <label>, aria-labelledby target. Returns null when the page
     supplies none, in which case no name is asserted. */
  function accessibleNameFor(select) {
    if (!select) return null;

    if (select.id) {
      var labels = document.querySelectorAll('label[for]');
      for (var i = 0; i < labels.length; i++) {
        if (labels[i].getAttribute('for') === select.id) {
          var viaFor = (labels[i].textContent || '').trim();
          if (viaFor) return viaFor;
        }
      }
    }

    var aria = select.getAttribute('aria-label');
    if (aria && aria.trim()) return aria.trim();

    var wrapping = select.closest('label');
    if (wrapping) {
      var wrapped = (wrapping.textContent || '').trim();
      if (wrapped) return wrapped;
    }

    var labelledby = select.getAttribute('aria-labelledby');
    if (labelledby) {
      var parts = labelledby.split(/\s+/).map(function (id) {
        var el = document.getElementById(id);
        return el ? (el.textContent || '').trim() : '';
      });
      var joined = parts.filter(Boolean).join(' ').trim();
      if (joined) return joined;
    }

    return null;
  }

  function optionItems(root) {
    return Array.prototype.slice.call(root.querySelectorAll('li.option'));
  }

  function syncState(root) {
    var isOpen = root.classList.contains('open');
    root.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

    var active = null;
    optionItems(root).forEach(function (li) {
      li.setAttribute('aria-selected', li.classList.contains('selected') ? 'true' : 'false');
      if (li.classList.contains('disabled')) {
        li.setAttribute('aria-disabled', 'true');
      } else {
        li.removeAttribute('aria-disabled');
      }
      if (li.classList.contains('focus')) active = li;
    });

    /* aria-activedescendant only while the popup is open: DOM focus stays on the
       combobox while NiceSelect moves its `.focus` class with the arrow keys. */
    if (isOpen && active && active.id) {
      root.setAttribute('aria-activedescendant', active.id);
    } else {
      root.removeAttribute('aria-activedescendant');
    }

    /* NiceSelect moves its `.focus` class with the arrow keys but never scrolls
       the highlighted option into view, so on a list long enough to overflow
       (`.nice-list` is `overflow-y: auto`) a keyboard user arrows the highlight
       straight out of sight. Scroll the CONTAINER only, and only when it really
       overflows, so the page itself never moves. */
    if (isOpen && active) {
      /* The scroll container is not the <ul>: base.css caps
         `.nice-select-dropdown` at max-height 350px, so walk up to whichever
         ancestor inside the widget actually overflows. */
      var container = active.parentElement;
      while (container && container !== root && container.scrollHeight <= container.clientHeight) {
        container = container.parentElement;
      }
      if (container && container !== root && container.scrollHeight > container.clientHeight) {
        var cRect = container.getBoundingClientRect();
        var aRect = active.getBoundingClientRect();
        if (aRect.top < cRect.top) {
          container.scrollTop -= cRect.top - aRect.top;
        } else if (aRect.bottom > cRect.bottom) {
          container.scrollTop += aRect.bottom - cRect.bottom;
        }
      }
    }
  }

  function enhance(root) {
    if (!root || root.hasAttribute(DONE_ATTR)) return;
    root.setAttribute(DONE_ATTR, '1');

    var select = nativeSelectFor(root);
    var list = root.querySelector('ul.nice-list') || root.querySelector('ul');

    root.setAttribute('role', 'combobox');
    root.setAttribute('aria-haspopup', 'listbox');
    root.setAttribute('aria-expanded', root.classList.contains('open') ? 'true' : 'false');
    if (root.classList.contains('disabled')) root.setAttribute('aria-disabled', 'true');

    var name = accessibleNameFor(select);
    if (name) root.setAttribute('aria-label', name);

    /* Carry over a description the page already renders for the native control
       (e.g. the sort controls' "a11y-refresh-page-message"), but only when the
       referenced element actually exists. */
    var describedby = select && select.getAttribute('aria-describedby');
    if (describedby) {
      var stillThere = describedby.split(/\s+/).filter(function (id) {
        return !!document.getElementById(id);
      });
      if (stillThere.length) root.setAttribute('aria-describedby', stillThere.join(' '));
    }

    if (list) {
      if (!list.id) list.id = nextId('qg-nice-listbox');
      list.setAttribute('role', 'listbox');
      if (name) list.setAttribute('aria-label', name);
      if (root.getAttribute('multiple') !== null || root.classList.contains('has-multiple')) {
        list.setAttribute('aria-multiselectable', 'true');
      }
      /* aria-controls only — no aria-owns: the listbox is already a DOM
         descendant of the combobox, so re-parenting it would be redundant. */
      root.setAttribute('aria-controls', list.id);
    }

    optionItems(root).forEach(function (li) {
      if (!li.id) li.id = nextId('qg-nice-option');
      li.setAttribute('role', 'option');
    });

    /* Group headings sit as flat siblings of the options rather than wrapping
       them, so they cannot be exposed as role="group" without lying about the
       structure; they are removed from the listbox's child list instead. */
    Array.prototype.slice.call(root.querySelectorAll('li.optgroup')).forEach(function (li) {
      li.setAttribute('role', 'presentation');
    });

    syncState(root);

    /* NiceSelect mutates classes only (`open` on the root, `selected`/`focus` on
       the items), so a class observer is the whole state channel. */
    var observer = new MutationObserver(function () {
      syncState(root);
    });
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
    });
  }

  function enhanceAll() {
    Array.prototype.slice.call(document.querySelectorAll(NS_SELECTOR)).forEach(enhance);
  }

  /* Every runtime re-render of a NiceSelect goes through `window.renderSelects`
     (facets.js, cart.js, infinite-scroll.js, product-info.js, product-compare.js,
     recently-viewed.liquid all call it by name at call time), so wrapping that
     one function covers every rebuild without a document-wide MutationObserver.
     The initial pass rides DOMContentLoaded: global.js is deferred from
     layout/theme.liquid's <head> and this file is deferred from the header
     section in <body>, so global.js registers its own DOMContentLoaded listener
     first and NiceSelect has already built the DOM when ours runs. */
  function wrapRenderSelects() {
    if (typeof window.renderSelects !== 'function' || window.renderSelects.qgA11yWrapped) return;
    var original = window.renderSelects;
    var wrapped = function () {
      var result = original.apply(this, arguments);
      enhanceAll();
      return result;
    };
    wrapped.qgA11yWrapped = true;
    window.renderSelects = wrapped;
  }

  function start() {
    wrapRenderSelects();
    enhanceAll();
  }

  /* Both passes are needed and neither is redundant: a deferred script executes
     while readyState is already "interactive", i.e. BEFORE DOMContentLoaded, so
     the immediate call finds nothing on a cold load, while global.js builds the
     widgets from its own DOMContentLoaded listener (registered first, so ours
     runs after it). The immediate call is what covers a late/async injection of
     this file, when DOMContentLoaded has already fired. */
  start();
  document.addEventListener('DOMContentLoaded', start);
  document.addEventListener('shopify:section:load', function () {
    wrapRenderSelects();
    enhanceAll();
  });

  /* ----------------------------------------------------------------
     Space to open / choose — the binding NiceSelect explicitly skips.
     Capture phase so it lands before the widget's own keydown handler.
     NiceSelect drives everything from click events, so synthesising the
     click it already listens for keeps its state machine authoritative.
  ---------------------------------------------------------------- */
  function isSpace(event) {
    return event.key === ' ' || event.key === 'Spacebar' || event.keyCode === 32;
  }

  document.addEventListener(
    'keydown',
    function (event) {
      if (!isSpace(event)) return;
      if (event.altKey || event.ctrlKey || event.metaKey) return;

      var target = event.target;
      if (!target || !target.closest) return;
      /* Never hijack a space typed into the searchable variant's text field. */
      if (target.closest('input, textarea, [contenteditable="true"]')) return;

      var root = target.closest(NS_SELECTOR);
      if (!root || root.classList.contains('disabled')) return;

      event.preventDefault();

      if (root.classList.contains('open')) {
        var focused = root.querySelector('li.option.focus');
        if (focused && !focused.classList.contains('disabled')) {
          focused.click();
        }
        /* Bubbles to the widget's own click handler, which collapses the popup —
           the same path Enter takes. */
      } else {
        root.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      }
      syncState(root);
    },
    true
  );
})();
